import { useEffect, useState } from "react";
import styled from "styled-components";
import AdminSidebar from "../../components/layout/admin-sidebar";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  fetchDepartments,
  removeDepartment,
} from "../../store/features/organisation/department-slice";
import TableView from "../../components/controls/table-view";
import { TextContainer, ContentWrapper } from "../../styles/shared/table";
import {
  fetchDepartmentById,
  updateDepartment,
  setNewSubDepartment,
  deleteDepartment,
  remooveSubDepartment,
  deleteSubDepartment,
  UpdateDepartmentPayload,
} from "../../store/features/organisation/admin-department-slice";
import Button from "../../components/controls/button";
import { ICON_ID } from "../../components/icons/google-icon";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { showSuccess } from "../../components/notification/toastr-actions";
import { requiredValidator } from "../../helpers/validators";
import EditableField from "../../components/controls/editable-field";
import { ColumnConfig } from "../../components/controls/table-view";
import { DrawerID } from "../../constants/drawer-constants";
import { openDrawer } from "../../store/features/drawer/drawer-slice";
import {
  openConfirm,
  closeConfirm,
} from "../../store/features/notification/confirm-slice";
import useScreenSize from "../../hooks/useScreenSize";
import { setCurrentPage } from "../../store/features/authentication/user-slice";
import { useNavigate } from "react-router-dom";
import { enumToOptions, pascalToLabel } from "../../helpers/enum-helper";
import { ApplicationOrderTemplate } from "../../models/organisation/application-order-template";
import { ApplicationOrderingMode } from "../../models/organisation/application-ordering-mode";

type DepartmentValidation = {
  [K in keyof ApplicationDepartment]?: string | null;
};

const AdminDepartment = () => {
  const dispatch = useAppDispatch();
  const departments = useAppSelector((state) => state.department.departments)?.departments ?? [];
  const selectedDepartment = useAppSelector(
    (state) => state.adminDepartment.selectedDepartment
  );
  const [validation, setValidation] = useState<DepartmentValidation>();

  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchDepartments()).then((action) => {
      if (
        fetchDepartments.fulfilled.match(action) &&
        action.payload.departments &&
        action.payload.departments.length > 0
        && selectedDepartment == null
      ) {
        const firstDepartment = action.payload.departments[0];
        if (firstDepartment) {
          handleDepartmentSelect(firstDepartment.id);
        }
      }
    });

    dispatch(setCurrentPage("Manage Departments"))
  }, [dispatch]);

  const handleDepartmentSelect = (departmentId: number) => {
    dispatch(fetchDepartmentById(departmentId));
  };

  const handleSave = async (
    payload: UpdateDepartmentPayload,
    validator: (value: string) => string | null
  ) => {
    const { field, value } = payload;

    const validation = validator(value)
    setValidation((prevState) => ({
      ...prevState,
      [field]: validation,
    }));

    if (validation) {
      return;
    }

    if (selectedDepartment) {
      const updatedDepartment: ApplicationDepartment = {
        ...selectedDepartment,
        [field]: value,
      };
      const response = await dispatch(updateDepartment(updatedDepartment));
      if (response) {
        dispatch(
          showSuccess(
            `Update Successful`
          )
        );
      }
    }
  };

  const handleCreateNewSubDepartment = () => {
    dispatch(setNewSubDepartment());
    dispatch(
      openDrawer({
        id: DrawerID.SubDepartment,
        anchor: "right",
        data: {
          headerText: "Add New Sub Department",
        },
      })
    );
  };

  const handleCreateNewDepartment = () => {
    dispatch(
      openDrawer({
        id: DrawerID.Department,
        anchor: "right",
        data: {
          headerText: "Add Department",
        },
      })
    );
  };
  const handleDeleteSubDepartmentClick = (row: any) => {
    const onConfirm = async () => {
      const response = await dispatch(deleteSubDepartment(row.id));
      if (response) {
        dispatch(remooveSubDepartment(row.id));
        dispatch(closeConfirm());
        dispatch(showSuccess(`${row.name} deleted successfully.`));
      }
    };
    const onCancel = () => {
      dispatch(closeConfirm());
    };
    dispatch(
      openConfirm({
        title: "Remove " + row.name + "?",
        message: "Are you sure you want to delete this sub department?",
        show: true,
        data: {
          onConfirm: onConfirm,
          onCancel: onCancel,
        },
      })
    );
  };
  const handleDeleteDepartmentClick = () => {
    if (!selectedDepartment?.id) {
      return;
    }
    const onConfirm = async () => {
      const response = await dispatch(deleteDepartment(selectedDepartment?.id));
      if (response) {
        const firstDepartment = departments[0];
        if (firstDepartment) {
          handleDepartmentSelect(firstDepartment.id);
        }
        dispatch(removeDepartment(selectedDepartment?.id));
        dispatch(closeConfirm());
        dispatch(
          showSuccess(`${selectedDepartment?.name} deleted successfully.`)
        );
      }
    };

    const onCancel = () => {
      dispatch(closeConfirm());
    };

    dispatch(
      openConfirm({
        title: "Remove " + selectedDepartment?.name + "?",
        message: "Are you sure you want to delete this department?",
        show: true,
        data: {
          onConfirm: onConfirm,
          onCancel: onCancel,
        },
      })
    );
  };
  const columnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true,
    },
    departmentId: {
      hidden: true,
    },
    canDelete: {
      hidden: true,
    },
    active: {
      hidden: true,
    },
    name: {
      order: 3,
      name: "Sub Department",
      render: (value: number) => <TextContainer>{value}</TextContainer>,
    },
    description: {
      order: 4,
      render: (value: number) => <ContentWrapper>{value}</ContentWrapper>,
    },
    productSelectionMode: {
      name: "Product Selection",
      order: 5,
      render: (value: string) => <ContentWrapper>{pascalToLabel(value)}</ContentWrapper>,
    },
  };
  const handleActivateDeactivateClick = async () => {
    if (!selectedDepartment) return;

    const updatedDepartment = {
      ...selectedDepartment,
      active: !selectedDepartment.active,
    };
    await dispatch(updateDepartment(updatedDepartment));
    dispatch(
      showSuccess(
        `${updatedDepartment.active ? "Enabled" : "Disabled"} ${updatedDepartment.name
        }`
      )
    );
  };
  const renderActionButton = (row: any) => {
    return (
      <ButtonWrapper>
        <Button
          variant={isMobile ? "text" : "outlined"}
          id={row.id}
          label={isMobile ? "" : "Edit"}
          icon={isMobile ? ICON_ID.Edit : undefined}
          isPrimary={true}
          fullWidth={false}
          onClick={() =>
            dispatch(
              openDrawer({
                id: DrawerID.SubDepartment,
                anchor: "right",
                data: {
                  id: row.id,
                  headerText: "Edit Sub Departments",
                },
              })
            )
          }
        />

        {row?.canDelete && (
          <Button
            variant="outlined"
            id={row.id}
            label="Delete"
            isDanger={true}
            fullWidth={false}
            onClick={() => handleDeleteSubDepartmentClick(row)}
          />
        )}
      </ButtonWrapper>
    );
  };
  return (
    <Container>
      <NavigationSection>
        <AdminSidebar
          items={departments.map((department) => ({
            label: department.name,
            action: () => handleDepartmentSelect(department.id),
            isSelected: department.id == selectedDepartment?.id
          }))}
        >
          {!isMobile && (
            <CreateNewButtonContainer>
              <Button
                variant="text"
                icon={ICON_ID.Add}
                id="btnCreateNewDepartment"
                label="New"
                onClick={handleCreateNewDepartment}
              />
            </CreateNewButtonContainer>
          )}
        </AdminSidebar>
      </NavigationSection>
      <ContentSection>
        <ContentTitle>
          <HeaderGridContainer>
            <EditableField
              type="text"
              value={selectedDepartment?.name || ""}
              error={validation?.name}
              label="Department Name"
              displayValue={selectedDepartment?.name}
              onSave={(savedValue) =>
                handleSave({
                  field: "name",
                  value: savedValue
                },
                  requiredValidator
                )
              }
            />
            <ButtonWrapper>
              {selectedDepartment?.canDelete ? (
                <Button
                  variant="outlined"
                  id={`delete-department-${selectedDepartment?.id}`}
                  label={"Delete"}
                  isDanger={true}
                  fullWidth={false}
                  onClick={handleDeleteDepartmentClick}
                />
              ) : (
                <Button
                  variant="text"
                  id={`active/inactive-department-${selectedDepartment?.id}`}
                  label={selectedDepartment?.active ? "Disable" : "Enable"}
                  isPrimary={!selectedDepartment?.active}
                  isDanger={selectedDepartment?.active}
                  onClick={handleActivateDeactivateClick}
                />
              )}
            </ButtonWrapper>
          </HeaderGridContainer>
        </ContentTitle>
        <Body>
          <FieldsGridContainer>
            <EditableField
              type="dropdown"
              value={selectedDepartment?.orderTemplate || ""}
              displayValue={`Order Template: ${pascalToLabel(selectedDepartment?.orderTemplate ?? "")}`}
              options={enumToOptions(ApplicationOrderTemplate)}
              label="Order Template"
              onSave={(savedValue) =>
                handleSave({
                  field: "orderTemplate",
                  value: savedValue
                },
                  requiredValidator
                )
              }
            />
          </FieldsGridContainer>

          <FieldsGridContainer>
            <EditableField
              type="dropdown"
              value={selectedDepartment?.orderingMode || ""}
              displayValue={`Ordering Mode: ${pascalToLabel(selectedDepartment?.orderingMode ?? "")}`}
              options={enumToOptions(ApplicationOrderingMode)}
              label="Ordering Mode"
              onSave={(savedValue) =>
                handleSave({
                  field: "orderingMode",
                  value: savedValue
                },
                  requiredValidator
                )
              }
            />
          </FieldsGridContainer>

          <TableContainer>
            <ButtonContainer>
              <Button
                variant="contained"
                icon={ICON_ID.Add}
                id="btnCreateNewSubDepartment"
                label={isMobile ? "Create New" : "New sub department"}
                fullWidth={false}
                onClick={(handleCreateNewSubDepartment)}
              />
            </ButtonContainer>

            <TableView
              id="subDepartmentTable"
              emptyText="No sub departments"
              totalCount={selectedDepartment?.subDepartments?.length ?? 0}
              data={selectedDepartment?.subDepartments ?? []}
              columnConfig={columnConfig}
              actionButton={renderActionButton}
              showHeaders={true}
            />
          </TableContainer>
        </Body>
      </ContentSection>
    </Container>
  );
};

const Body = styled.div`
  padding: 10px;
  height: 100%;
`;
const Container = styled.div`
  display: flex;
  margin: 10px;
  column-gap: 20px;
  
  height: 97%;
  @media (max-width: 1600px) {
    flex-direction: column;
  }
`;

const TableContainer = styled.div`
  padding: 5px;
  @media (max-width: 700px) {
    padding: 0px;
  }
`;

const ContentTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  border-bottom: 2px solid ${(props) => props.theme.application.scheme.primary};
  padding: 5px 0px 5px 5px;
`;

const NavigationSection = styled.div`
  min-width: 15%;
`;

const ContentSection = styled.div`
  display: flex;
  box-shadow: ${(props) => props.theme.application.boxShadow};
  flex-direction: column;
  width: 100%;
  padding: 8px;
  background: ${(props) => props.theme.application.background};
  border-radius: 4px;
  height: 100%;
`;
const ButtonWrapper = styled.div`  
float:right;
`;

const HeaderGridContainer = styled.div`
  display:flex;
  div:first-of-type{
    flex:1;
  }
`;


const FieldsGridContainer = styled.div`
  display: flex;      
  flex-direction:column;

`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 10px 0px;
  align-items: center;
`;

const CreateNewButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
export default AdminDepartment;
